/**
 * Hero Block TypeScript
 * Handles the rotating word animation
 */

import { TextRotator } from '../modules/text-rotator';

class HeroWordRotator extends TextRotator {
  private animationTimeouts: Map<HTMLElement, number> = new Map();

  constructor() {
    // Call parent constructor with hero-specific config
    super('[data-hero-words]', {
      itemSelector: '.word-item',
      interval: 3000
    });
  }

  protected rotateItems(
    container: HTMLElement,
    items: NodeListOf<Element>,
    currentIndex: number
  ): void {
    const nextIndex = (currentIndex + 1) % items.length;
    const currentItem = items[currentIndex] as HTMLElement;
    const nextItem = items[nextIndex] as HTMLElement;
    
    // Clear any existing animation timeouts for these items
    this.clearAnimationTimeout(currentItem);
    this.clearAnimationTimeout(nextItem);
    
    // Animate out current item
    currentItem.classList.remove('opacity-100');
    currentItem.classList.add('opacity-0', 'zoom-out');
    
    // Clean up zoom-out class after animation
    const currentTimeout = window.setTimeout(() => {
      currentItem.classList.remove('zoom-out');
      this.animationTimeouts.delete(currentItem);
    }, 800);
    this.animationTimeouts.set(currentItem, currentTimeout);

    // Animate in next item
    nextItem.classList.remove('opacity-0');
    nextItem.classList.add('opacity-100', 'zoom-in');
    
    // Clean up zoom-in class after animation
    const nextTimeout = window.setTimeout(() => {
      nextItem.classList.remove('zoom-in');
      this.animationTimeouts.delete(nextItem);
    }, 800);
    this.animationTimeouts.set(nextItem, nextTimeout);
  }

  private clearAnimationTimeout(element: HTMLElement): void {
    const timeout = this.animationTimeouts.get(element);
    if (timeout) {
      clearTimeout(timeout);
      this.animationTimeouts.delete(element);
    }
  }

  public destroy(): void {
    // Clear all animation timeouts
    this.animationTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    this.animationTimeouts.clear();
    
    // Call parent destroy
    super.destroy();
  }
}

// Initialize all hero blocks
let heroInstance: HeroWordRotator | null = null;

function initAllHeroBlocks(): void {
  // Destroy previous instance if exists
  if (heroInstance) {
    heroInstance.destroy();
  }
  
  // Create new instance
  const heroContainers = document.querySelectorAll('[data-hero-words]');
  if (heroContainers.length > 0) {
    heroInstance = new HeroWordRotator();
  }
}

// Check if we're in the WordPress block editor
const isBlockEditor = () => {
  return !!(
    window.wp &&
    window.wp.blocks &&
    window.wp.element &&
    document.body.classList.contains('block-editor-page')
  );
};

// Initialize based on context
if (isBlockEditor()) {
  // We're in the block editor - use WordPress APIs
  if (window.wp && window.wp.domReady) {
    // Initial load
    window.wp.domReady(() => {
      initAllHeroBlocks();
    });

    // Re-initialize when blocks are updated
    if (window.wp.data && window.wp.data.subscribe) {
      let previousBlocks: string = '';

      window.wp.data.subscribe(() => {
        // Check if we have the block editor store
        const blockEditorStore = window.wp?.data?.select('core/block-editor');
        if (!blockEditorStore) return;

        // Get current blocks
        const blocks = blockEditorStore.getBlocks();
        const blocksString = JSON.stringify(blocks);

        // Only re-init if blocks actually changed
        if (blocksString !== previousBlocks) {
          previousBlocks = blocksString;

          // Wait for DOM to update
          setTimeout(() => {
            initAllHeroBlocks();
          }, 100);
        }
      });
    }
  }
} else {
  // Frontend - use standard window load
  window.addEventListener('load', () => {
    initAllHeroBlocks();
  });
}